import { useEffect } from "react";
import { useConsole } from "../components/common/console/Console";
import { renewUserSession } from "./auth/user";
import { logout, setLoginOpen } from "./redux/authSlice";
import { useAppSelector } from "./redux/hooks";
import { selectOffline, setCachingForOffline } from "./redux/shuteSlice";
import { AppDispatch } from "./redux/store";
import { AJAXSettings, fnJSONParse, getJwtToken, setJwtToken, setRnwToken } from "./shared";

export const useComponentLoadUnload = (componentName:string, callonload?:()=> void,callonunload?:()=> void) => {
  useEffect(() => {
    console.log(`${componentName} Component Mounted`);
    if (callonload) callonload()
    return () => {
      console.log(`${componentName} Component Unmounted`)
      if (callonunload) callonunload()};
  }, [componentName]); // Passing componentName as a dependency in case it changes, it's optional if the name is static
};

 /**
 * [fnAJAXFetch ]
 * @param  ptAjaxSettings                        {perc-percentage complete;status-the tooltip}
 * @return                      nothing
 */
 export async function fnAJAXFetch(ptAjaxSettings:AJAXSettings) {
  const { fnConsoleError } = useConsole();
  const isOffline = await getOfflineStatusFromDB()
  const headers = {
    'Content-Type': ptAjaxSettings.contentType || '',
    'Authorization': ptAjaxSettings.authorization || `JWT ${getJwtToken()}`,
    'Accept': ptAjaxSettings.returnContentType === 'text' ? 'text/plain, */*;q=0.8'
        : ptAjaxSettings.returnContentType === 'blob' ? 'text/html, text/plain, */*;q=0.8'
        : 'application/json, text/plain, */*;q=0.8'
  };

  // Merge custom headers
  if (ptAjaxSettings.customHeaders) {
    Object.assign(headers, ptAjaxSettings.customHeaders);
  }
  // text/html, application/xhtml+xml, application/xml;q=0.9, image/webp, */*;q=0.8
  var tRequestInfo:RequestInit = {
      method: ptAjaxSettings.method, //'POST', // *GET, POST, PUT, DELETE, etc.
      mode: ptAjaxSettings.mode, // no-cors, *cors, same-origin
      cache: ptAjaxSettings.cache, // *default, no-cache, reload, force-cache, only-if-cached
      credentials: ptAjaxSettings.credentials,
      headers: headers,
      redirect: ptAjaxSettings.redirect,
      referrerPolicy: ptAjaxSettings.referrerPolicy,
      body: ['GET', 'HEAD'].includes(ptAjaxSettings.method || 'GET') ? undefined : JSON.stringify(ptAjaxSettings.data) // GET/HEAD must not carry a body — fetch() throws synchronously otherwise
  }
  try {
    if (isOffline && import.meta.env.VITE_OFFLINE_ENABLED === 'true') {
      // Simulate API response with dummy data
      if (ptAjaxSettings?.outcome_callback) {
        const cachedResponse = await getCache(ptAjaxSettings.url, true); // Example ok value
        ptAjaxSettings.outcome_callback(true, 200, cachedResponse, {}, ptAjaxSettings.callerID);
      } else {
        console.log(`no outcome_callback function defined`);
      };
      return;
    }    
    const MAX_RETRIES = ptAjaxSettings.authorization ? 1 : 2;
    let attempt = 0;
  
    while (attempt < MAX_RETRIES) {      
      attempt++;
      try {    
        // console.log(`Send AJAX: ${ptAjaxSettings.url}; ${JSON.stringify(tRequestInfo.headers)}`)
        var oAJAXResponse = await fetch(ptAjaxSettings.url,tRequestInfo)
        //proceed once the first promise is resolved.
        const oData = await (async () => {
          switch (ptAjaxSettings.returnContentType) {
            case 'text':
              // console.log('reading text')
              const sTextContent = await oAJAXResponse.text()
              // console.log(sTextContent)
              return sTextContent
            case 'blob':
              return await oAJAXResponse.blob();
            default:
              // or json
              const sContent = await oAJAXResponse.text()
              // console.log(sContent)
              try {
                let tContent = fnJSONParse(sContent)
                // console.log(tContent)
                return tContent
              } catch (eError) {
                if (ptAjaxSettings.dispatch) {
                  fnConsoleError(ptAjaxSettings.dispatch,eError,sContent)
                } else {
                  console.error(eError)
                }
                return sContent
              }
            }
          })();

          if ([401, 403].includes(oAJAXResponse.status)) {
            // can we trigger a renew?
            const renewed = await renewUserSession(ptAjaxSettings.dispatch);
            if (!renewed.value.outcome ||  (!renewed.value.content?.refreshnoerror && renewed.value.error)) {
              attempt=MAX_RETRIES
              throw new Error('Session renewal failed');
            }
            setJwtToken(renewed.value.content.outcome.token)
            setRnwToken(renewed.value.content.outcome.payload.rnw)
            // continue the loop to retry the request
            tRequestInfo.headers = {
                'Content-Type': ptAjaxSettings.contentType||'',
                'Authorization': ptAjaxSettings.authorization||`JWT ${getJwtToken()}`,
                'Accept': ptAjaxSettings.returnContentType == 'text' ? 'text/plain, */*;q=0.8'
                    : ptAjaxSettings.returnContentType == 'blob' ? 'text/html, text/plain, */*;q=0.8'
                    : 'application/json, text/plain, */*;q=0.8'
              }
            continue;
          }
  

          if (ptAjaxSettings?.outcome_callback) {
            if (ptAjaxSettings.dispatch && import.meta.env.VITE_OFFLINE_ENABLED === 'true' && import.meta.env.VITE_OFFLINE_CACHELIVE === 'true') {
              await setCache(ptAjaxSettings.url, oData, oAJAXResponse.ok, oAJAXResponse.headers,ptAjaxSettings.dispatch);
            };
            ptAjaxSettings.outcome_callback(oAJAXResponse.ok,oAJAXResponse.status,oData,oAJAXResponse.headers, ptAjaxSettings.callerID)

          } else {
            console.log(`no outcome_callback function defined: ${oAJAXResponse.ok},${oAJAXResponse.status}, ${oData}`)
          };
          return;
       
  
        } catch (error) {
          if (attempt >= MAX_RETRIES) {
            // If max retries reached, throw the error
            throw error;
          }
        }
    }    
    if (ptAjaxSettings.dispatch) {
      try {
        ptAjaxSettings.dispatch(setLoginOpen(true)); // Dispatch action to open login modal
      } catch (oErr) {
        console.log('Cant dispoatch')
      }
    }

  } catch (eError) {
    if (ptAjaxSettings.dispatch) {
      fnConsoleError(ptAjaxSettings.dispatch,eError)
    }
    if (ptAjaxSettings?.outcome_callback) {
      ptAjaxSettings.outcome_callback(false,500, eError,{},ptAjaxSettings.callerID)
    } else {
      console.log(`no outcome_callback function defined: false, 500, ${eError}`)
    };
  };

};
 /**
 * [fnAJAXFetch ]
 * @param  ptAjaxSettings                        {perc-percentage complete;status-the tooltip}
 * @return                      nothing
 */
 export async function fnAJAXFetchPromise(ptAjaxSettings: AJAXSettings): Promise<any> {
  const isOffline = await getOfflineStatusFromDB()
 
  // text/html, application/xhtml+xml, application/xml;q=0.9, image/webp, */*;q=0.8
  var tRequestInfo:RequestInit = {
      method: ptAjaxSettings.method, //'POST', // *GET, POST, PUT, DELETE, etc.
      mode: ptAjaxSettings.mode, // no-cors, *cors, same-origin
      cache: ptAjaxSettings.cache, // *default, no-cache, reload, force-cache, only-if-cached
      credentials: ptAjaxSettings.credentials,
      headers: {
        'Content-Type': ptAjaxSettings.contentType||'',
        'Authorization': ptAjaxSettings.authorization||`JWT ${getJwtToken()}`,
        'Accept': ptAjaxSettings.returnContentType == 'text' ? 'text/plain, */*;q=0.8'
            : ptAjaxSettings.returnContentType == 'blob' ? 'text/html, text/plain, */*;q=0.8'
            : 'application/json, text/plain, */*;q=0.8'
      },
      redirect: ptAjaxSettings.redirect,
      referrerPolicy: ptAjaxSettings.referrerPolicy,
      body: ['GET', 'HEAD'].includes(ptAjaxSettings.method || 'GET') ? undefined : JSON.stringify(ptAjaxSettings.data) // GET/HEAD must not carry a body — fetch() throws synchronously otherwise
  }
  try {
    if (isOffline && import.meta.env.VITE_OFFLINE_ENABLED === 'true') {
      // Simulate API response with dummy data
      if (ptAjaxSettings?.outcome_callback) {
        const cachedResponse = await getCache(ptAjaxSettings.url, true); // Example ok value
        ptAjaxSettings.outcome_callback(true, 200, cachedResponse, {}, ptAjaxSettings.callerID);
      } else {
        console.log(`no outcome_callback function defined`);
      };
      return;
    }    
    const MAX_RETRIES = ptAjaxSettings.authorization ? 1 : 2;
    let attempt = 0;
  
    while (attempt < MAX_RETRIES) {      
      attempt++;
      try {
        
        const oAJAXResponse = await fetch(ptAjaxSettings.url, tRequestInfo);
        // console.log(oAJAXResponse,await oAJAXResponse.text(),tRequestInfo)
        if ([401, 403].includes(oAJAXResponse.status)) {
          // can we trigger a renew?
          const renewed = await renewUserSession(ptAjaxSettings.dispatch);
          if (!renewed.value.outcome ||  (!renewed.value.content?.refreshnoerror && renewed.value.error)) {
            attempt=MAX_RETRIES
            throw new Error('Session renewal failed');
          }
          setJwtToken(renewed.value.content.outcome.token)
          setRnwToken(renewed.value.content.outcome.payload.rnw)
          // continue the loop to retry the request
          tRequestInfo.headers = {
              'Content-Type': ptAjaxSettings.contentType||'',
              'Authorization': ptAjaxSettings.authorization||`JWT ${getJwtToken()}`,
              'Accept': ptAjaxSettings.returnContentType == 'text' ? 'text/plain, */*;q=0.8'
                  : ptAjaxSettings.returnContentType == 'blob' ? 'text/html, text/plain, */*;q=0.8'
                  : 'application/json, text/plain, */*;q=0.8'
            }
          continue;
        }
  

        // Process the response based on the expected content type
        let responseData;
        switch (ptAjaxSettings.returnContentType) {
          case 'text':
            responseData = await oAJAXResponse.text();
            break;
          case 'blob':
            responseData = await oAJAXResponse.blob();
            break;
          default:
            const responseText = await oAJAXResponse.text();
            try {
              responseData = JSON.parse(responseText);
            } catch (eError) {
              // Handle JSON parsing error
              responseData = responseText;
            }
            break;
        }

        // Return the response data
        return {
          success: oAJAXResponse.ok,
          status: oAJAXResponse.status,
          data: responseData,
          headers: oAJAXResponse.headers
        };
      } catch (error) {
        if (attempt >= MAX_RETRIES) {
          throw error; // Rethrow the error if max retries reached
        }
      }
    }
  } catch (eError) {
    // Handle error and return a failure response
    return { success: false, status: 500, data: eError };
  }
}
// async function callAPI(method, url, tParams, dispatch) {
//   const SputnikAPIBaseURL = "/"; // Replace with your base URL
//   const MAX_RETRIES = 2; // Set your max retries
//   let attempt = 0;
//   const callerID = generateUUID(); // Implement this to generate a unique ID for each request

//   while (attempt < MAX_RETRIES) {
//       try {
//           let response = await fetch(`${SputnikAPIBaseURL}api/javelin/${url}`, {
//               method: method,
//               mode: "cors",
//               credentials: "include",
//               headers: {
//                   'Content-Type': 'application/json', // Assuming JSON is the default
//                   'Authorization': `JWT ${getJwtToken()}`, // Implement getJwtToken
//                   'Accept': 'application/json, text/plain, */*;q=0.8' // Default accept header
//               },
//               body: JSON.stringify({ ...tParams, callerID }) // Include callerID in the request body
//           });

//           if ([401, 403].includes(response.status)) {
//               const renewed = await renewUserSession(dispatch); // Implement renewUserSession
//               if (!renewed.value.outcome || (!renewed.value.content?.refreshnoerror && renewed.value.error)) {
//                   throw new Error('Session renewal failed');
//               }
//               setJwtToken(renewed.value.content.outcome.token); // Implement setJwtToken
//               setRnwToken(renewed.value.content.outcome.payload.rnw); // Implement setRnwToken
//               attempt++;
//               continue;
//           }

//           const responseData = await response.json(); // Assuming JSON response
//           return { success: response.ok, status: response.status, data: responseData };
//       } catch (error) {
//           if (attempt >= MAX_RETRIES) {
//               console.error(error);
//               return { success: false, status: 500, data: error };
//           }
//           attempt++;
//       }
//   }
// }

function generateUUID() { // Public Domain/MIT
  var d = new Date().getTime(); //Timestamp
  var d2 = (performance && performance.now && (performance.now()*1000)) || 0; //Time in microseconds since page-load or 0 if unsupported
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      var r = Math.random() * 16; //random number between 0 and 16
      if(d > 0){ //Use timestamp until depleted
          r = (d + r)%16 | 0;
          d = Math.floor(d/16);
      } else { //Use microseconds since page-load if supported
          r = (d2 + r)%16 | 0;
          d2 = Math.floor(d2/16);
      }
      return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
  });
}

export async function setCache(url: string, data: any, ok: boolean, headers?: Headers, dispatch?: AppDispatch) {
  return new Promise<void>(async (resolve, reject) => {
    if (dispatch)
      {dispatch(setCachingForOffline(true));} // Cache is being updated
    const db = await openDB("SputnikAPI_Dummy");
    const tx = db.transaction("SputnikAPI_Dummy", "readwrite");
    const store = tx.objectStore("SputnikAPI_Dummy");

    // Convert headers to a plain object to store in IndexedDB
    const headersObj: { [key: string]: string } = {};
    if (headers)
     {
      headers.forEach((value, key) => {
        headersObj[key] = value;
      })
    }

    // Use the combination of URL and ok as the key
    const key = `${url}-${ok}`;
    store.put({ data, ok, headers: headersObj }, key);

    // Handling transaction completion
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    if (dispatch)
      {dispatch(setCachingForOffline(false));} // Cache is not being updated
  });
}


export async function getCache(url: string, ok: boolean) {
  return new Promise<{ data: any, ok: boolean, headers: Headers } | null>(async (resolve, reject) => {
    const db = await openDB("SputnikAPI_Dummy");
    const tx = db.transaction("SputnikAPI_Dummy", "readonly");
    const store = tx.objectStore("SputnikAPI_Dummy");

    // Use the combination of URL and ok as the key
    const key = `${url}-${ok}`;
    const request = store.get(key);

    request.onsuccess = () => {
      const result = request.result;
      if (result) {
        resolve({ data: result.data, ok: result.ok, headers: new Headers(result.headers) });
      } else {
        resolve(null);
      }
    };

    request.onerror = () => reject(request.error);
  });
}


// Open (or create) the SputnikAPI_Dummy database
function openDB(name: string) {
  return new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(name, 1);
    
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = function() {
      const db = request.result;
      db.createObjectStore("SputnikAPI_Dummy"); // Create objectStore named "SputnikAPI_Dummy"
    };
  });
}

// Writing to IndexedDB
export const setOfflineStatusInDB = async (status: boolean) => {
  await setCache("sputnik_offline", status, false);
};

// Reading from IndexedDB
export const getOfflineStatusFromDB = async () => {
  var bOffline = false;
  try {
    const tReturn = await getCache("sputnik_offline", false); // Example ok value
    if (tReturn) {
      bOffline = tReturn.data
    }
  
  } catch (oError) {}

  return bOffline
};


export function deepMerge(original: any, update: any): any {
  let output = Object.assign({}, original);
  if (isObject(original) && isObject(update)) {
      Object.keys(update).forEach(key => {
          if (isObject(update[key])) {
              if (!(key in original))
                  Object.assign(output, { [key]: update[key] });
              else
                  output[key] = deepMerge(original[key], update[key]);
          } else {
              Object.assign(output, { [key]: update[key] });
          }
      });
  }
  return output;
}

/**
 * Checks if the given item is an object and not an array.
 *
 * @param {any} item The item to check.
 * @returns {boolean} True if the item is an object and not an array, false otherwise.
 */
export function isObject(item: any): boolean {
  return (item && typeof item === 'object' && !Array.isArray(item));
}
